import { DateRange } from "ui/components/DateRangePicker";

/** Query paramovi za API pozive — dodaje from/to samo kad su prisutni ("Cijeli period" -> prazno). */
export const buildRangeParams = (from?: Date | null, to?: Date | null): string => {
  const params = new URLSearchParams();
  if (from) params.set("from", from.toISOString());
  if (to) params.set("to", to.toISOString());
  const s = params.toString();
  return s ? `?${s}` : "";
};

/** Je li datum unutar raspona (null granice = otvoreno). */
export const inRange = (date: Date | null | undefined, range: DateRange): boolean => {
  if (!date) return false;
  if (range.from && date < range.from) return false;
  if (range.to && date > range.to) return false;
  return true;
};

/**
 * Prorjeđivanje oznaka na osi — vraća `label` samo za ~maxTicks ravnomjerno raspoređenih pozicija
 * (i uvijek zadnju), inače prazan string. Sprječava zbijanje kod dnevnih/mjesečnih bucketa.
 */
export const thinLabel = (label: string, i: number, n: number, maxTicks = 6): string => {
  if (n <= maxTicks) return label;
  const step = Math.ceil(n / maxTicks);
  return i % step === 0 || i === n - 1 ? label : "";
};

export type BucketGranularity = "day" | "month" | "quarter";

export interface RangeBucket {
  key: string;      // stabilni ključ (npr. "2026-05" / "2026-05-14" / "2026-Q2")
  label: string;    // kratka oznaka za os
  start: Date;
  end: Date;        // ekskluzivno
}

const MONTH_LABELS = ["Sij", "Velj", "Ožu", "Tra", "Svi", "Lip", "Srp", "Kol", "Ruj", "Lis", "Stu", "Pro"];

/**
 * Granularnost prema §8.1:
 *  - ≤ 31 dan -> dnevno
 *  - ≤ 24 mjeseca -> mjesečno
 *  - > 24 mjeseca -> kvartalno
 * Za "Cijeli period" (from null) koristi se `fallbackFrom` (npr. najstariji datum u podacima).
 */
export const resolveGranularity = (from: Date, to: Date): BucketGranularity => {
  const days = Math.round((to.getTime() - from.getTime()) / 86400000);
  if (days <= 31) return "day";
  const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
  if (months <= 24) return "month";
  return "quarter";
};

/** Generira bucket-e za raspon [from, to] prema granularnosti. */
export const resolveBuckets = (from: Date, to: Date): RangeBucket[] => {
  const granularity = resolveGranularity(from, to);
  const buckets: RangeBucket[] = [];

  if (granularity === "day") {
    let d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const last = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    while (d <= last) {
      const start = new Date(d);
      const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      buckets.push({
        key: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`,
        label: `${start.getDate()}.${start.getMonth() + 1}.`,
        start,
        end,
      });
      d = end;
    }
    return buckets;
  }

  if (granularity === "month") {
    let y = from.getFullYear();
    let m = from.getMonth();
    const endBoundary = new Date(to.getFullYear(), to.getMonth() + 1, 1);
    let cursor = new Date(y, m, 1);
    while (cursor < endBoundary) {
      const start = new Date(y, m, 1);
      const end = new Date(y, m + 1, 1);
      buckets.push({
        key: `${y}-${String(m + 1).padStart(2, "0")}`,
        label: MONTH_LABELS[m],
        start,
        end,
      });
      m += 1;
      if (m > 11) { m = 0; y += 1; }
      cursor = new Date(y, m, 1);
    }
    return buckets;
  }

  // kvartalno
  let y = from.getFullYear();
  let q = Math.floor(from.getMonth() / 3);
  const endBoundary = new Date(to.getFullYear(), to.getMonth() + 1, 1);
  let cursor = new Date(y, q * 3, 1);
  while (cursor < endBoundary) {
    const start = new Date(y, q * 3, 1);
    const end = new Date(y, q * 3 + 3, 1);
    buckets.push({
      key: `${y}-Q${q + 1}`,
      label: `Q${q + 1} '${String(y).slice(2)}`,
      start,
      end,
    });
    q += 1;
    if (q > 3) { q = 0; y += 1; }
    cursor = new Date(y, q * 3, 1);
  }
  return buckets;
};
